import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface PDFExportOptions {
  filename?: string;
  quality?: number;
  format?: 'a4' | 'letter';
  orientation?: 'portrait' | 'landscape';
  scale?: number;
}

export interface PortfolioTotals {
  totalP1: number;
  totalP2: number;
  totalP3P4: number;
  totalIncidentViaAlerts: number;
  totalIncidentViaChange: number;
  avgAvailability: number;
  avgSLOObjective: number;
  avgErrorBudget: number;
}

export interface PortfolioApp {
  appId: string;
  platformPortfolio: string;
  platform: string;
  application: string;
  fullStackMonitoring: number;
  signalToNoise: string;
  p1p2: number;
  p3p4mi: number;
  incidentViaAlerts: string;
  incidentsCausedByChange: string;
  sloAvailabilityMtd: number;
  sloObjective: number;
  ebInMinutes: number;
}

export class PDFExportService {
  /**
   * Export HTML element to PDF
   * @param element - The HTML element to export
   * @param options - PDF export options
   */
  static async exportToPDF(
    element: HTMLElement, 
    options: PDFExportOptions = {}
  ): Promise<void> {
    const {
      filename = 'alert-analysis-report.pdf',
      quality = 1.0,
      format = 'a4',
      orientation = 'portrait',
      scale = 2
    } = options;

    try {
      // Create canvas from HTML element
      const canvas = await html2canvas(element, {
        scale: scale, // Use configurable scale
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        width: element.scrollWidth,
        height: element.scrollHeight,
        onclone: (clonedDoc) => {
          // Ensure proper styling in cloned document
          const clonedElement = clonedDoc.querySelector('[data-pdf-export]');
          if (clonedElement) {
            (clonedElement as HTMLElement).style.transform = 'none';
            (clonedElement as HTMLElement).style.overflow = 'visible';
          }
        }
      });

      // Get image data
      const imgData = canvas.toDataURL('image/png', quality);
      
      // Calculate dimensions
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Create PDF
      const pdf = new jsPDF({
        orientation,
        unit: 'px',
        format: [imgWidth, imgHeight]
      });

      // Add image to PDF
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      
      // Save PDF
      pdf.save(filename);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error('Failed to generate PDF report. Please try again.');
    }
  }

  /**
   * Export AI insights modal content to PDF
   * @param options - PDF export options
   */
  static async exportAIInsightsToPDF(options: PDFExportOptions = {}): Promise<void> {
    const modalContent = document.querySelector('[data-ai-insights-content]') as HTMLElement;
    
    if (!modalContent) {
      throw new Error('AI insights content not found. Please ensure the AI analysis is visible.');
    }

    const {
      filename = `ai-insights-report-${new Date().toISOString().split('T')[0]}.pdf`,
      ...restOptions
    } = options;

    await this.exportToPDF(modalContent, { filename, ...restOptions });
  }

  /**
   * Export entire dashboard to PDF
   * @param options - PDF export options
   */
  static async exportDashboardToPDF(options: PDFExportOptions = {}): Promise<void> {
    const dashboardContent = document.querySelector('[data-dashboard-content]') as HTMLElement;
    
    if (!dashboardContent) {
      throw new Error('Dashboard content not found. Please ensure the dashboard is loaded.');
    }

    const {
      filename = `alert-dashboard-report-${new Date().toISOString().split('T')[0]}.pdf`,
      orientation = 'landscape', // Dashboard is better in landscape
      ...restOptions
    } = options;

    await this.exportToPDF(dashboardContent, { filename, orientation, ...restOptions });
  }

  /**
   * Create a formatted PDF report with header and footer
   * @param element - The HTML element to export
   * @param options - PDF export options with additional metadata
   */
  static async exportFormattedReport(
    element: HTMLElement,
    options: PDFExportOptions & {
      title?: string;
      subtitle?: string;
      generatedBy?: string;
      maxPages?: number;
    } = {}
  ): Promise<void> {
    const {
      filename = 'alert-analysis-report.pdf',
      title = 'Alert Analysis Report',
      subtitle = 'AI-Powered Signal to Noise Analysis',
      generatedBy = 'Maersk Reliability Framework',
      format = 'a4',
      orientation = 'portrait',
      maxPages = 2
    } = options;

    try {
      // Create canvas from HTML element
      const canvas = await html2canvas(element, {
        scale: 1.5, // Reduced from 2 to 1.5 for smaller file size
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        width: element.scrollWidth, // Allow full width to capture all content
        height: element.scrollHeight, // Allow full height to capture all content
        imageTimeout: 15000,
        removeContainer: true, // Clean up after rendering
        foreignObjectRendering: false, // More stable rendering
        scrollX: 0, // Ensure we start from the top-left
        scrollY: 0  // Ensure we start from the top-left
      });

      // Create PDF with proper dimensions
      const pdf = new jsPDF({
        orientation: orientation as 'portrait' | 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate image dimensions for multi-page PDF
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const pxToMm = 0.264583; // Conversion factor

      const availableHeightMm = maxPages === 1 ? pageHeight - 30 : (pageHeight - (orientation === 'landscape' ? 40 : 60)); // Minimize header space for single page to maximize content area

      // Calculate how many pages we need (limit to maximum specified pages)
      const calculatedPixelsPerPage = availableHeightMm / pxToMm;
      const calculatedPages = Math.ceil(imgHeight / calculatedPixelsPerPage);
      const numPages = maxPages === 1 ? 1 : Math.min(calculatedPages, maxPages); // For single page, force to 1 page with scaling
      
      // Adjust pixels per page to fit content across the limited number of pages
      const pixelsPerPage = maxPages === 1 ? imgHeight : (numPages > 1 ? imgHeight / numPages : calculatedPixelsPerPage);

      // Calculate scaling ratio to fit content on page(s)
      const widthRatio = (pageWidth - 40) / (imgWidth * pxToMm);
      let ratio = Math.min(widthRatio, 1.0); // Don't upscale, only downscale if needed
      
      // For single page export, also consider height to ensure all content fits
      if (maxPages === 1) {
        const heightRatio = availableHeightMm / (imgHeight * pxToMm);
        ratio = Math.min(ratio, heightRatio); // Use the more restrictive ratio
      }

      const finalWidth = imgWidth * pxToMm * ratio;

      // Generate multi-page PDF
      for (let pageNum = 0; pageNum < numPages; pageNum++) {
        // Add new page for pages after the first
        if (pageNum > 0) {
          pdf.addPage();
        }

        // Add header for each page (minimal header to avoid duplication with HTML content)
        if (pageNum === 0) {
          // First page: show full title and subtitle since HTML content may not have headers
          pdf.setFontSize(maxPages === 1 ? 14 : 16); // Smaller title for single page
          pdf.setTextColor(0, 103, 179);
          pdf.text(title, 20, 12);
          
          pdf.setFontSize(maxPages === 1 ? 10 : 12); // Smaller subtitle for single page
          pdf.setTextColor(108, 117, 125);
          pdf.text(subtitle, 20, 20);
          
          pdf.setFontSize(8); // Smaller generation text for single page
          pdf.setTextColor(108, 117, 125);
          pdf.text(`Generated: ${new Date().toLocaleString()} | By: ${generatedBy}`, 20, maxPages === 1 ? 25 : 35);
        } else {
          // Subsequent pages: show full header for consistency
          pdf.setFontSize(16);
          pdf.setTextColor(0, 103, 179);
          pdf.text(title, 20, 15);
          
          pdf.setFontSize(12);
          pdf.setTextColor(108, 117, 125);
          pdf.text(subtitle, 20, 25);
          
          pdf.setFontSize(10);
          pdf.setTextColor(108, 117, 125);
          pdf.text(`Page ${pageNum + 1} of ${numPages} | Generated: ${new Date().toLocaleString()}`, 20, 35);
        }

        // Add separator line (moved down to accommodate full header)
        const separatorY = maxPages === 1 ? 25 : (orientation === 'landscape' ? 30 : 40); // Minimize header space for single page
        pdf.setDrawColor(225, 229, 233);
        pdf.line(20, separatorY, pageWidth - 20, separatorY); // Adjusted for orientation

        // Calculate which portion of the image to show on this page
        let sourceY = pageNum * pixelsPerPage;
        let sourceHeight = Math.min(pixelsPerPage, imgHeight - sourceY);
        
        // For single page export, capture entire content
        if (maxPages === 1) {
          sourceY = 0;
          sourceHeight = imgHeight;
        }

        // Create a temporary canvas for this page segment
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        tempCanvas.width = imgWidth;
        tempCanvas.height = sourceHeight;

        if (tempCtx) {
          // Draw the portion of the original canvas for this page
          tempCtx.drawImage(
            canvas,
            0, sourceY, imgWidth, sourceHeight, // Source rectangle
            0, 0, imgWidth, sourceHeight // Destination rectangle
          );

          const pageImgData = tempCanvas.toDataURL('image/jpeg', 0.8);

          // Calculate final dimensions for this page segment
          const segmentHeightMm = sourceHeight * pxToMm * ratio;
          const yPos = separatorY + 5; // Start content below the separator line

          // Add the page segment image
          pdf.addImage(pageImgData, 'JPEG', 20, yPos, finalWidth, segmentHeightMm);
        }

        // Add page number footer
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Page ${pageNum + 1} of ${numPages}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }
      
      // Save PDF
      pdf.save(filename);
      
    } catch (error) {
      console.error('Error generating formatted PDF:', error);
      throw new Error('Failed to generate PDF report. Please try again.');
    }
  }

  /**
   * Export Portfolio Health table data to PDF
   * @param data - Array of portfolio applications
   * @param options - PDF export options
   */
  static async exportPortfolioTableToPDF(
    data: PortfolioApp[],
    options: PDFExportOptions & {
      selectedPortfolio?: string;
      totals?: PortfolioTotals;
    } = {}
  ): Promise<void> {
    const {
      filename = `portfolio-health-data-${new Date().toISOString().split('T')[0]}.pdf`,
      selectedPortfolio = 'All Portfolios',
      orientation = 'landscape',
      format = 'a4',
      totals
    } = options;

    try {
      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableWidth = pageWidth - (margin * 2);
      const usableHeight = pageHeight - (margin * 2);

      // Header
      pdf.setFontSize(16);
      pdf.setTextColor(0, 103, 179); // Maersk blue
      pdf.text('MVC Application Health Details', margin, margin + 10);
      
      pdf.setFontSize(12);
      pdf.setTextColor(108, 117, 125);
      pdf.text(`Portfolio: ${selectedPortfolio}`, margin, margin + 18);
      pdf.text(`Total Applications: ${data.length}`, margin, margin + 25);
      
      pdf.setFontSize(10);
      pdf.text(`Generated on: ${new Date().toLocaleString()}`, margin, margin + 32);
      pdf.text('Generated by: Maersk Reliability Framework', margin, margin + 38);

      // Table setup
      let yPosition = margin + 50;
      const rowHeight = 10; // Keep data rows at 10 for good readability
      const headerHeight = 18; // Increased to 18mm for consistent header spacing across all pages

      // Column widths (optimized - reduced oversized columns, ensured EB visibility)
      const colWidths = [
        18, // App ID (keep as is)
        35, // Platform Portfolio (keep as is - needs space for long names)
        28, // Platform (keep as is)
        40, // Application (keep as is - needs space for long names)
        14, // Health Score (reduced from 16)
        16, // Full Stack Monitoring (reduced from 20 - you said too much space)
        14, // Signal to Noise (reduced from 18 - you said too much space)
        8,  // P1/P2 (reduced from 10 - you said too much space)
        10, // P3/P4(MI) (reduced from 12 - you said too much space)
        14, // Incident via Alerts (reduced from 18 - you said too much space)
        20, // Incidents due to Change (reduced from 25 - you said too much space)
        28, // SLO Availability (MTD) (keep as is)
        18, // SLO Objective (keep as is)
        15  // EB (minutes) (increased from 12 to ensure visibility)
      ];

      // Calculate total width to ensure it fits
      const totalColWidth = colWidths.reduce((sum, width) => sum + width, 0);
      console.log('Total column width:', totalColWidth, 'Available width:', usableWidth);

      // Headers
      const headers = [
        'App ID',
        'Platform Portfolio',
        'Platform', 
        'Application',
        'Health Score',
        'Full Stack Mon.',
        'Signal to Noise',
        'P1/P2',
        'P3/P4(MI)',
        'Incident via Alerts',
        'Incidents due to Change',
        'SLO Availability (MTD)',
        'SLO Objective',
        'EB (min)'
      ];

      // Draw header
      pdf.setFillColor(248, 249, 250); // Light gray background
      pdf.rect(margin, yPosition, usableWidth, headerHeight, 'F');
      
      pdf.setFontSize(8);
      pdf.setTextColor(44, 62, 80);
      pdf.setFont('helvetica', 'bold');

      let xPosition = margin;
      headers.forEach((header, index) => {
        const cellWidth = colWidths[index];
        pdf.text(header, xPosition + 1, yPosition + 11, { maxWidth: cellWidth - 2 });
        
        // Draw vertical line
        if (index < headers.length - 1) {
          pdf.setDrawColor(225, 229, 233);
          pdf.line(xPosition + cellWidth, yPosition, xPosition + cellWidth, yPosition + headerHeight);
        }
        
        xPosition += cellWidth;
      });

      // Draw header border
      pdf.setDrawColor(225, 229, 233);
      pdf.rect(margin, yPosition, usableWidth, headerHeight);

      yPosition += headerHeight;

      // Data rows
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);

      let pageCount = 1;
      const maxRowsPerPage = Math.floor((usableHeight - 60) / rowHeight);

      data.forEach((app, index) => {
        // Check if we need a new page
        if (index > 0 && index % maxRowsPerPage === 0) {
          pdf.addPage();
          pageCount++;
          yPosition = margin + 10;
          
          // Redraw header on new page
          pdf.setFillColor(248, 249, 250);
          pdf.rect(margin, yPosition, usableWidth, headerHeight, 'F');
          
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(8);
          
          xPosition = margin;
          headers.forEach((header, headerIndex) => {
            const cellWidth = colWidths[headerIndex];
            pdf.text(header, xPosition + 1, yPosition + 11, { maxWidth: cellWidth - 2 });
            
            if (headerIndex < headers.length - 1) {
              pdf.setDrawColor(225, 229, 233);
              pdf.line(xPosition + cellWidth, yPosition, xPosition + cellWidth, yPosition + headerHeight);
            }
            
            xPosition += cellWidth;
          });
          
          pdf.setDrawColor(225, 229, 233);
          pdf.rect(margin, yPosition, usableWidth, headerHeight);
          yPosition += headerHeight;
          
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(7);
        }

        // Row background (alternating)
        if (index % 2 === 1) {
          pdf.setFillColor(252, 252, 252);
          pdf.rect(margin, yPosition, usableWidth, rowHeight, 'F');
        }

        // Row data
        const rowData = [
          app.appId,
          app.platformPortfolio,
          app.platform,
          app.application,
          `${app.fullStackMonitoring * 25}`, // Convert to health score
          `${app.fullStackMonitoring}/4`,
          app.signalToNoise,
          app.p1p2.toString(),
          app.p3p4mi.toString(),
          app.incidentViaAlerts,
          app.incidentsCausedByChange,
          app.sloAvailabilityMtd > 0 ? `${app.sloAvailabilityMtd.toFixed(2)}%` : 'N/A',
          app.sloObjective > 0 ? `${app.sloObjective.toFixed(2)}%` : 'N/A',
          app.ebInMinutes > 0 ? app.ebInMinutes.toString() : 'N/A'
        ];

        xPosition = margin;
        rowData.forEach((cellData, cellIndex) => {
          const cellWidth = colWidths[cellIndex];
          
          // Text color based on values
          let textColor = [44, 62, 80]; // Default dark
          if (cellIndex === 7 && app.p1p2 > 0) textColor = [220, 53, 69]; // P1/P2 red if > 0
          if (cellIndex === 8 && app.p3p4mi > 0) textColor = [255, 193, 7]; // P3/P4 yellow if > 0
          
          pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
          
          // Handle very long text by truncating only if absolutely necessary
          let displayText = cellData;
          if (cellData.length > 35 && cellIndex === 3) { // Application name - only if very long
            displayText = cellData.substring(0, 32) + '...';
          }
          // Don't truncate Platform Portfolio (index 1) and Platform (index 2) anymore
          
          pdf.text(displayText, xPosition + 1, yPosition + 6, { 
            maxWidth: cellWidth - 2,
            align: 'left'
          });
          
          // Draw vertical line
          if (cellIndex < rowData.length - 1) {
            pdf.setDrawColor(225, 229, 233);
            pdf.line(xPosition + cellWidth, yPosition, xPosition + cellWidth, yPosition + rowHeight);
          }
          
          xPosition += cellWidth;
        });

        // Draw row border
        pdf.setDrawColor(225, 229, 233);
        pdf.rect(margin, yPosition, usableWidth, rowHeight);

        yPosition += rowHeight;
      });

      // Add Total Row if totals data is provided
      if (totals) {
        // Add some spacing before total row
        yPosition += 2;
        
        // Total row with normal background (same as other rows)
        pdf.setFillColor(252, 252, 252); // Light gray background like alternating rows
        pdf.rect(margin, yPosition, usableWidth, rowHeight + 2, 'F');
        
        // Total row border (normal gray like other rows)
        pdf.setDrawColor(225, 229, 233);
        pdf.setLineWidth(0.5);
        pdf.rect(margin, yPosition, usableWidth, rowHeight + 2);
        
        // Total row data
        const totalRowData = [
          'TOTAL',
          '-',
          '-', 
          '-',
          '-',
          '-',
          '-',
          (totals.totalP1 + totals.totalP2).toString(),
          totals.totalP3P4.toString(),
          totals.totalIncidentViaAlerts.toString(),
          totals.totalIncidentViaChange.toString(),
          totals.avgAvailability > 0 ? `${totals.avgAvailability.toFixed(2)}%` : 'NA',
          totals.avgSLOObjective > 0 ? `${totals.avgSLOObjective.toFixed(2)}%` : 'NA',
          totals.avgErrorBudget > 0 ? `${Math.round(totals.avgErrorBudget).toLocaleString()} min` : 'NA'
        ];

        pdf.setFont('helvetica', 'bold'); // Bold text for all total row
        pdf.setFontSize(7);
        pdf.setTextColor(44, 62, 80); // Normal black text color

        let xPosition = margin;
        totalRowData.forEach((cellData, cellIndex) => {
          const cellWidth = colWidths[cellIndex];
          
          pdf.text(cellData, xPosition + 1, yPosition + 7, { 
            maxWidth: cellWidth - 2,
            align: 'left'
          });
          
          // Draw vertical line (normal gray)
          if (cellIndex < totalRowData.length - 1) {
            pdf.setDrawColor(225, 229, 233);
            pdf.line(xPosition + cellWidth, yPosition, xPosition + cellWidth, yPosition + rowHeight + 2);
          }
          
          xPosition += cellWidth;
        });

        yPosition += rowHeight + 2;
        pdf.setFont('helvetica', 'normal'); // Reset to normal font
        pdf.setTextColor(44, 62, 80); // Keep normal text color
      }

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        `Page ${pageCount} | Generated by Maersk Reliability Framework | ${new Date().toLocaleString()}`,
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' }
      );

      // Save PDF
      pdf.save(filename);

    } catch (error) {
      console.error('Error generating portfolio table PDF:', error);
      throw new Error('Failed to generate portfolio table PDF. Please try again.');
    }
  }

  /**
   * Export full dashboard page to PDF
   * @param element - The HTML element containing the full dashboard
   * @param options - PDF export options
   */
  static async exportFullPageToPDF(
    element: HTMLElement,
    options: PDFExportOptions = {}
  ): Promise<void> {
    const {
      filename = 'portfolio-health-dashboard.pdf',
      quality = 0.9
    } = options;

    try {
      // High quality capture
      const canvas = await html2canvas(element, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false
      });

      // Get image data
      const imgData = canvas.toDataURL('image/png', quality);
      
      // Get original image dimensions
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const aspectRatio = imgHeight / imgWidth;
      
      // A4 landscape layout
      const pageWidth = 297; // A4 landscape width in mm
      const pageHeight = 210; // A4 landscape height in mm
      const margin = 5; // Very small margins to maximize content space
      
      const availableWidth = pageWidth - (margin * 2);
      const availableHeight = pageHeight - (margin * 2);
      
      // Use ALL available space - maximize content size
      let finalWidth = availableWidth; // Use maximum width
      let finalHeight = availableWidth * aspectRatio; // Calculate height based on max width
      
      // If calculated height exceeds page, use max height instead
      if (finalHeight > availableHeight) {
        finalHeight = availableHeight; // Use maximum height
        finalWidth = availableHeight / aspectRatio; // Recalculate width
      }
      
      // Center the content on the page
      const xOffset = (pageWidth - finalWidth) / 2;
      const yOffset = (pageHeight - finalHeight) / 2;
      
      // Create PDF
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // Add image using maximum possible size
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, finalWidth, finalHeight);
      
      // Save PDF
      pdf.save(filename);
      
    } catch (error) {
      console.error('Error generating full page PDF:', error);
      throw new Error('Failed to generate full page PDF. Please try again.');
    }
  }

  /**
   * Export AI Incident Insights to a properly formatted PDF with text content
   * @param aiInsightsData - The AI insights data object
   * @param selectedTimeRange - The time range for the analysis
   * @param options - PDF export options
   */
  static async exportAIIncidentInsightsToPDF(
    aiInsightsData: any,
    selectedTimeRange: string,
    options: PDFExportOptions = {}
  ): Promise<void> {
    const {
      filename = `ai-incident-insights-${new Date().toISOString().split('T')[0]}.pdf`,
      orientation = 'portrait',
    } = options;

    try {
      const pdf = new jsPDF({
        orientation: orientation as 'portrait' | 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - 2 * margin;
      let yPos = margin;
      const lineHeight = 7;
      const smallLineHeight = 5;

      // Helper function to check if we need a new page
      const checkPageBreak = (requiredSpace: number = lineHeight) => {
        if (yPos + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Helper function to add text with wrapping
      const addText = (text: string, fontSize: number, isBold: boolean = false, color: [number, number, number] = [0, 0, 0], indent: number = 0) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.setTextColor(color[0], color[1], color[2]);
        
        const lines = pdf.splitTextToSize(text, maxWidth - indent);
        for (const line of lines) {
          checkPageBreak();
          pdf.text(line, margin + indent, yPos);
          yPos += fontSize < 10 ? smallLineHeight : lineHeight;
        }
      };

      // Add header with logo space
      pdf.setFillColor(0, 103, 179); // Maersk blue
      pdf.rect(0, 0, pageWidth, 35, 'F');
      
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(255, 255, 255);
      pdf.text('AI Incident Analysis & Insights', margin, 15);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Maersk Operational Excellence Dashboard', margin, 25);

      yPos = 45;

      // Executive Summary Section
      addText('Executive Summary', 16, true, [0, 103, 179]);
      yPos += 5;
      
      addText(`Analysis Period: ${selectedTimeRange || 'Not specified'}`, 10, false);
      addText(`Incidents Analyzed: 95`, 10, false);
      addText(`Applications Analyzed: ${aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.applications_analyzed || 0}`, 10, false);
      addText(`Report Generated: ${new Date().toLocaleString()}`, 10, false);
      yPos += 5;

      // Performance Summary
      if (aiInsightsData?.data?.performance_summary) {
        checkPageBreak(20);
        addText('Performance Summary', 14, true, [0, 103, 179]);
        yPos += 3;
        addText(aiInsightsData.data.performance_summary, 10, false);
        yPos += 5;
      }

      // Application Health Overview
      checkPageBreak(20);
      addText('Application Health Overview', 14, true, [0, 103, 179]);
      yPos += 3;

      const mvcSummary = aiInsightsData?.data?.postmortem_insights?.monitoring_correlation?.mvc_status_summary;
      if (mvcSummary) {
        addText('Application Breakdown:', 12, true);
        addText(`• Total Applications: ${mvcSummary.total_applications || 0}`, 10, false, [0, 0, 0], 5);
        addText(`• MVC Applications: ${mvcSummary.mvc_applications || 0}`, 10, false, [0, 0, 0], 5);
        addText(`• Non-MVC Applications: ${mvcSummary.non_mvc_applications || 0}`, 10, false, [0, 0, 0], 5);
        addText(`• Applications with Monitoring: ${mvcSummary.monitoring_tracking_available || 0}`, 10, false, [0, 0, 0], 5);
        addText(`• Applications without Monitoring: ${mvcSummary.monitoring_tracking_unavailable || 0}`, 10, false, [0, 0, 0], 5);
        yPos += 5;
      }

      // Postmortem Insights & Analysis header
      const hasPostmortemInsights = aiInsightsData?.data?.postmortem_insights;
      if (hasPostmortemInsights) {
        checkPageBreak(20);
        addText('Postmortem Insights & Analysis', 14, true, [0, 103, 179]);
        yPos += 5;
      }

      // Action Items Analysis
      const actionItemsAnalysis = aiInsightsData?.data?.postmortem_insights?.action_items_analysis;
      if (actionItemsAnalysis) {
        checkPageBreak(25);
        addText('Action Items Analysis', 14, true, [0, 103, 179]);
        yPos += 3;

        const actionTypes = actionItemsAnalysis.action_type_distribution || {};
        const totalActions = Object.values(actionTypes).reduce((sum: number, count: unknown) => sum + (Number(count) || 0), 0);
        const openCount = (Number(actionTypes['To Do']) || 0) + (Number(actionTypes['In Progress']) || 0) + (Number(actionTypes['General']) || 0);

        addText(`• Total Action Items: ${totalActions}`, 10, false, [0, 0, 0], 5);
        addText(`• Open Actions: ${openCount}`, 10, false, [0, 0, 0], 5);
        addText(`• Completed Actions: ${actionTypes.Done || 0}`, 10, false, [0, 0, 0], 5);
        addText(`• In Progress Actions: ${actionTypes['In Progress'] || 0}`, 10, false, [0, 0, 0], 5);
        addText(`• To Do Actions: ${actionTypes['To Do'] || 0}`, 10, false, [0, 0, 0], 5);
        addText(`• High Priority Actions: ${actionItemsAnalysis.high_priority_actions_count || 0}`, 10, false, [0, 0, 0], 5);
        addText(`• Overdue Actions: ${actionItemsAnalysis.overdue_actions_count || 0}`, 10, false, [0, 0, 0], 5);
        yPos += 3;

        // Priority Breakdown
        if (actionItemsAnalysis.priority_breakdown) {
          checkPageBreak(15);
          addText('Priority Breakdown:', 12, true);
          addText(`• P1 (Critical): ${actionItemsAnalysis.priority_breakdown.p1 || 0}`, 10, false, [0, 0, 0], 5);
          addText(`• P2 (High): ${actionItemsAnalysis.priority_breakdown.p2 || 0}`, 10, false, [0, 0, 0], 5);
          addText(`• P3 (Medium): ${actionItemsAnalysis.priority_breakdown.p3 || 0}`, 10, false, [0, 0, 0], 5);
          addText(`• P4 (Low): ${actionItemsAnalysis.priority_breakdown.p4 || 0}`, 10, false, [0, 0, 0], 5);
          yPos += 3;
        }

        // Status Distribution
        if (actionItemsAnalysis.status_breakdown) {
          checkPageBreak(15);
          addText('Status Distribution:', 12, true);
          addText(`• Open: ${actionItemsAnalysis.status_breakdown.open || 0}`, 10, false, [0, 0, 0], 5);
          addText(`• In Progress: ${actionItemsAnalysis.status_breakdown.in_progress || 0}`, 10, false, [0, 0, 0], 5);
          addText(`• Under Review: ${actionItemsAnalysis.status_breakdown.under_review || 0}`, 10, false, [0, 0, 0], 5);
          addText(`• Completed: ${actionItemsAnalysis.status_breakdown.completed || 0}`, 10, false, [0, 0, 0], 5);
          yPos += 5;
        }
      }

      // Monitoring Correlation
      const monitoringCorrelation = aiInsightsData?.data?.postmortem_insights?.monitoring_correlation;
      if (monitoringCorrelation) {
        checkPageBreak(20);
        addText('Monitoring Correlation', 14, true, [0, 103, 179]);
        yPos += 3;

        addText(`• Applications Analyzed: ${monitoringCorrelation.applications_analyzed || 0}`, 10, false, [0, 0, 0], 5);
        addText(`• Monitoring Data Available: ${monitoringCorrelation.monitoring_data_available ? 'Yes' : 'No'}`, 10, false, [0, 0, 0], 5);
        addText(`• Correlation Success Rate: Available for analysis`, 10, false, [0, 0, 0], 5);
        addText(`• Mean Time to Detection: Tracking enabled for monitored applications`, 10, false, [0, 0, 0], 5);
        yPos += 3;

        // Alert Effectiveness Improvements
        const alertImprovements = monitoringCorrelation.incident_prevention_opportunities?.alert_effectiveness_improvements;
        if (alertImprovements && alertImprovements.length > 0) {
          checkPageBreak(15);
          addText('Alert Effectiveness Improvements:', 12, true);
          alertImprovements.forEach((item: any) => {
            checkPageBreak(10);
            addText(`• ${item.action}`, 10, true, [0, 0, 0], 5);
            addText(`  Current Rate: ${item.current_rate}`, 9, false, [0, 0, 0], 10);
            addText(`  Target Rate: ${item.target_rate}`, 9, false, [0, 0, 0], 10);
            addText(`  Missed Incidents: ${item.missed_incidents}`, 9, false, [0, 0, 0], 10);
          });
          yPos += 3;
        }

        // Root Cause Monitoring Gaps
        const rootCauseGaps = monitoringCorrelation.root_cause_monitoring_gaps?.action_items;
        if (rootCauseGaps && rootCauseGaps.length > 0) {
          checkPageBreak(15);
          addText('Root Cause Monitoring Gaps:', 12, true);
          rootCauseGaps.forEach((item: any) => {
            checkPageBreak(15);
            addText(`• ${item.affected_applications}`, 10, true, [0, 0, 0], 5);
            addText(`  Root Cause: ${item.root_cause}`, 9, false, [0, 0, 0], 10);
            if (item.missing_capabilities) {
              addText(`  Missing Capabilities: ${item.missing_capabilities.join(', ')}`, 9, false, [0, 0, 0], 10);
            }
            if (item.priority) addText(`  Priority: ${item.priority}`, 9, false, [0, 0, 0], 10);
            if (item.incident_count) addText(`  Incident Count: ${item.incident_count}`, 9, false, [0, 0, 0], 10);
            if (item.action) addText(`  Action: ${item.action}`, 9, false, [0, 0, 0], 10);
          });
          yPos += 5;
        }
      }

      // Top Root Causes
      const topRootCauses = aiInsightsData?.data?.postmortem_insights?.root_cause_analysis?.top_root_causes;
      if (topRootCauses && topRootCauses.length > 0) {
        checkPageBreak(15);
        addText('Top Root Causes:', 12, true);
        topRootCauses.forEach((cause: string, index: number) => {
          checkPageBreak();
          addText(`${index + 1}. ${cause}`, 10, false, [0, 0, 0], 5);
        });
        yPos += 5;
      }

      // Reliability & Postmortem Correlation
      const reliabilityCorr = aiInsightsData?.data?.postmortem_insights?.reliability_postmortem_correlation;
      if (reliabilityCorr) {
        checkPageBreak(25);
        addText('Reliability & Postmortem Correlation', 14, true, [0, 103, 179]);
        yPos += 3;

        if (reliabilityCorr.current_portfolio) {
          const portfolio = reliabilityCorr.current_portfolio;
          addText(`${portfolio.name || 'Current Portfolio'}:`, 12, true);
          addText(`• Postmortems Found: ${portfolio.postmortem_count || 0}`, 10, false, [0, 0, 0], 5);
          addText(`• Action Items Total: ${portfolio.action_items_count || 0}`, 10, false, [0, 0, 0], 5);
          addText(`• Completed Actions: ${portfolio.action_items_summary?.done || 0}`, 10, false, [0, 0, 0], 5);
          addText(`• In Progress Actions: ${portfolio.action_items_summary?.in_progress || 0}`, 10, false, [0, 0, 0], 5);
          addText(`• Action Items Summary: ${portfolio.action_items_summary?.total || 0} total actions tracked`, 10, false, [0, 0, 0], 5);
          yPos += 3;
        }

        if (reliabilityCorr.other_portfolios) {
          checkPageBreak(15);
          addText('Cross-Portfolio Impact:', 12, true);
          addText(`• External Action Items: ${reliabilityCorr.other_portfolios.action_items_count || 0}`, 10, false, [0, 0, 0], 5);
          if (reliabilityCorr.other_portfolios.cross_portfolio_impact?.description) {
            addText(`• Cross-Portfolio Issues: ${reliabilityCorr.other_portfolios.cross_portfolio_impact.description}`, 10, false, [0, 0, 0], 5);
          }
          const portfoliosInvolved = reliabilityCorr.other_portfolios.portfolios_involved?.filter((p: string) => p && p.trim()).join(', ') || 'None';
          addText(`• Other Portfolios Involved: ${portfoliosInvolved}`, 10, false, [0, 0, 0], 5);
          addText(`• Postmortems in Other Portfolios: ${reliabilityCorr.other_portfolios.postmortems?.length || 0}`, 10, false, [0, 0, 0], 5);
          
          // Add Total Incidents Analyzed, Total Postmortems Found, and Data Source
          if (reliabilityCorr.total_incidents_analyzed !== undefined) {
            addText(`• Total Incidents Analyzed: ${reliabilityCorr.total_incidents_analyzed}`, 10, false, [0, 0, 0], 5);
          }
          if (reliabilityCorr.total_postmortems_found !== undefined) {
            addText(`• Total Postmortems Found: ${reliabilityCorr.total_postmortems_found}`, 10, false, [0, 0, 0], 5);
          }
          // Always show Data Source
          const dataSource = reliabilityCorr.data_source || 'Spring PM API';
          addText(`• Data Source: ${dataSource}`, 10, false, [0, 0, 0], 5);
          yPos += 5;
        }
      }

      // Application-Specific Monitoring Gaps
      const rootCauseGapsDetails = monitoringCorrelation?.root_cause_monitoring_gaps?.action_items;
      if (rootCauseGapsDetails && rootCauseGapsDetails.length > 0) {
        const appDetails = rootCauseGapsDetails.flatMap((item: any) => item.affected_apps_details || []).filter(Boolean);
        if (appDetails.length > 0) {
          checkPageBreak(15);
          addText('Application-Specific Monitoring Gaps:', 12, true);
          appDetails.forEach((app: any) => {
            checkPageBreak(20);
            addText(`• ${app.application}`, 10, true, [0, 0, 0], 5);
            if (app.root_cause) addText(`  Root Cause: ${app.root_cause}`, 9, false, [0, 0, 0], 10);
            if (app.missing_capabilities) addText(`  Missing Capabilities: ${app.missing_capabilities.join(', ')}`, 9, false, [0, 0, 0], 10);
            if (app.specific_recommendations) addText(`  Recommendations: ${app.specific_recommendations.join(', ')}`, 9, false, [0, 0, 0], 10);
            if (app.monitoring_available !== undefined) addText(`  Monitoring Available: ${app.monitoring_available ? 'Yes' : 'No'}`, 9, false, [0, 0, 0], 10);
            if (app.is_mvc !== undefined) addText(`  MVC Status: ${app.is_mvc ? 'MVC Application' : 'Non-MVC Application'}`, 9, false, [0, 0, 0], 10);
          });
          yPos += 5;
        }
      }

      // Technical Roadmap
      const technicalRoadmap = aiInsightsData?.data?.technical_roadmap;
      if (technicalRoadmap && (technicalRoadmap.immediate_actions?.length || technicalRoadmap.short_term_improvements?.length || technicalRoadmap.long_term_optimization?.length)) {
        checkPageBreak(20);
        addText('Technical Roadmap', 14, true, [0, 103, 179]);
        yPos += 3;

        if (technicalRoadmap.immediate_actions && technicalRoadmap.immediate_actions.length > 0) {
          checkPageBreak(10);
          addText('Immediate Actions (0-30 days):', 12, true);
          technicalRoadmap.immediate_actions.forEach((action: string) => {
            checkPageBreak();
            addText(`• ${action}`, 10, false, [0, 0, 0], 5);
          });
          yPos += 3;
        }

        if (technicalRoadmap.short_term_improvements && technicalRoadmap.short_term_improvements.length > 0) {
          checkPageBreak(10);
          addText('Short-term Improvements (1-2 months):', 12, true);
          technicalRoadmap.short_term_improvements.forEach((improvement: string) => {
            checkPageBreak();
            addText(`• ${improvement}`, 10, false, [0, 0, 0], 5);
          });
          yPos += 3;
        }

        if (technicalRoadmap.long_term_optimization && technicalRoadmap.long_term_optimization.length > 0) {
          checkPageBreak(10);
          addText('Long-term Optimization (3-12 months):', 12, true);
          technicalRoadmap.long_term_optimization.forEach((optimization: string) => {
            checkPageBreak();
            addText(`• ${optimization}`, 10, false, [0, 0, 0], 5);
          });
          yPos += 5;
        }
      }

      // Root Cause Analysis
      const rootCause = aiInsightsData?.data?.root_cause;
      if (rootCause && (rootCause.predicted_root_cause || rootCause.related_components)) {
        checkPageBreak(20);
        addText('Root Cause Analysis', 14, true, [0, 103, 179]);
        yPos += 3;

        if (rootCause.predicted_root_cause) {
          addText(`• Predicted Root Cause: ${rootCause.predicted_root_cause}`, 10, false, [0, 0, 0], 5);
        }
        if (rootCause.related_components && rootCause.related_components.length > 0) {
          addText(`• Related Components: ${rootCause.related_components.join(', ')}`, 10, false, [0, 0, 0], 5);
        }
        yPos += 5;
      }

      // AI Recommendations
      const recommendation = aiInsightsData?.data?.recommendation;
      if (recommendation && (recommendation.action_priority || recommendation.expected_resolution_time || recommendation.recommended_action)) {
        checkPageBreak(20);
        addText('AI Recommendations', 14, true, [0, 103, 179]);
        yPos += 3;

        if (recommendation.action_priority) {
          addText(`• Priority: ${recommendation.action_priority}`, 10, false, [0, 0, 0], 5);
        }
        if (recommendation.expected_resolution_time) {
          addText(`• Expected Resolution Time: ${recommendation.expected_resolution_time}`, 10, false, [0, 0, 0], 5);
        }
        if (recommendation.recommended_action) {
          addText(`• Recommended Action: ${recommendation.recommended_action}`, 10, false, [0, 0, 0], 5);
        }
        yPos += 5;
      }

      // Prediction & Outlook
      const prediction = aiInsightsData?.data?.prediction;
      console.log('🔮 Prediction data:', prediction);
      if (prediction && (prediction.predicted_outcome || prediction.technical_indicators || prediction.time_to_impact)) {
        checkPageBreak(25);
        addText('Prediction & Outlook', 14, true, [0, 103, 179]);
        yPos += 3;

        if (prediction.predicted_outcome) {
          addText(prediction.predicted_outcome, 10, false, [0, 0, 0], 0);
          yPos += 3;
        }

        if (prediction.technical_indicators) {
          checkPageBreak(10);
          addText('Technical Indicators', 12, true);
          yPos += 2;
          addText(prediction.technical_indicators, 10, false, [0, 0, 0], 0);
          yPos += 3;
        }

        if (prediction.time_to_impact) {
          checkPageBreak(10);
          addText('Time to Impact', 12, true);
          yPos += 2;
          addText(prediction.time_to_impact, 10, false, [0, 0, 0], 0);
          yPos += 5;
        }
      }

      // Reliability Improvement Plan
      const reliabilityImprovement = aiInsightsData?.data?.reliability_improvement;
      console.log('📈 Reliability Improvement data:', reliabilityImprovement);
      if (reliabilityImprovement && (reliabilityImprovement.priority_level || reliabilityImprovement.impact_estimate || reliabilityImprovement.business_justification || reliabilityImprovement.technical_specifications)) {
        checkPageBreak(25);
        addText('Reliability Improvement Plan', 14, true, [0, 103, 179]);
        yPos += 3;

        if (reliabilityImprovement.priority_level) {
          addText('Priority Level:', 12, true);
          addText(reliabilityImprovement.priority_level, 10, false, [0, 0, 0], 5);
          yPos += 3;
        }

        if (reliabilityImprovement.impact_estimate) {
          checkPageBreak(10);
          addText('Impact Estimate:', 12, true);
          addText(reliabilityImprovement.impact_estimate, 10, false, [0, 0, 0], 5);
          yPos += 3;
        }

        if (reliabilityImprovement.business_justification) {
          checkPageBreak(10);
          addText('Business Justification:', 12, true);
          addText(reliabilityImprovement.business_justification, 10, false, [0, 0, 0], 5);
          yPos += 3;
        }

        if (reliabilityImprovement.technical_specifications) {
          checkPageBreak(10);
          addText('Technical Specifications:', 12, true);
          addText(reliabilityImprovement.technical_specifications, 10, false, [0, 0, 0], 5);
          yPos += 5;
        }
      }

      // Add footer to all pages
      const totalPages = (pdf as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        pdf.text('Maersk Operational Excellence Dashboard', margin, pageHeight - 10);
      }

      // Save the PDF - this triggers automatic download
      console.log('📄 Saving PDF:', filename);
      pdf.save(filename);
      console.log('✅ PDF download triggered successfully');
      
    } catch (error) {
      console.error('Error generating AI insights PDF:', error);
      throw new Error('Failed to generate AI insights PDF. Please try again.');
    }
  }
}